import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Generic sortable / filterable / paginated table with XLS/PDF export buttons.
// Built for the CSE market data tabs, but has no CSE-specific knowledge —
// everything about a column (how to render it, sort it, or export it) comes
// from the `columns` prop.

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const selectClass = 'rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
const inputClass = 'w-56 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
const btnClass = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50';

const IconDownload = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M10 12.5a.75.75 0 00.75-.75V4.56l1.97 1.97a.75.75 0 101.06-1.06l-3.25-3.25a.75.75 0 00-1.06 0L6.22 5.47a.75.75 0 101.06 1.06l1.97-1.97v7.19c0 .414.336.75.75.75z" />
    <path d="M3.5 12.75a.75.75 0 01.75.75v2.5c0 .414.336.75.75.75h10a.75.75 0 00.75-.75v-2.5a.75.75 0 011.5 0v2.5A2.25 2.25 0 0115 18.5H5a2.25 2.25 0 01-2.25-2.25v-2.5a.75.75 0 01.75-.75z" />
  </svg>
);

const IconPdf = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);

const IconSort = ({ direction }) => (
  <span className="inline-flex flex-col ml-1 -space-y-1 align-middle text-slate-300">
    <svg viewBox="0 0 12 6" className={`w-2.5 h-2.5 ${direction === 'asc' ? 'text-indigo-600' : ''}`} fill="currentColor"><path d="M6 0l6 6H0z" /></svg>
    <svg viewBox="0 0 12 6" className={`w-2.5 h-2.5 ${direction === 'desc' ? 'text-indigo-600' : ''}`} fill="currentColor"><path d="M6 6L0 0h12z" /></svg>
  </span>
);

const defaultAccessor = (row, col) => row[col.key];

const toComparable = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  const asNumber = Number(value);
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(asNumber)) return asNumber;
  return String(value).toLowerCase();
};

const DataTable = ({
  columns,
  rows,
  loading = false,
  emptyLabel = 'No data.',
  asOf,
  filename = 'export',
  getRowKey,
}) => {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: null, direction: 'asc' });

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const value = (col.filterValue || defaultAccessor)(row, col);
        return value !== null && value !== undefined && String(value).toLowerCase().includes(needle);
      })
    );
  }, [rows, search, columns]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const accessor = col.sortValue || col.filterValue || defaultAccessor;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = toComparable(accessor(a, col));
      const bv = toComparable(accessor(b, col));
      if (av < bv) return sort.direction === 'asc' ? -1 : 1;
      if (av > bv) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sorted, currentPage, pageSize]
  );

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };

  const downloadXls = () => {
    const exportRows = sorted.map((row) => {
      const out = {};
      columns.forEach((col) => {
        const accessor = col.exportValue || col.sortValue || col.filterValue || defaultAccessor;
        out[col.label] = accessor(row, col);
      });
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const downloadPdf = () => {
    const head = [columns.map((col) => col.label)];
    const body = sorted.map((row) =>
      columns.map((col) => {
        const accessor = col.exportValue || col.sortValue || col.filterValue || defaultAccessor;
        const value = accessor(row, col);
        return value === null || value === undefined ? '-' : String(value);
      })
    );
    const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
    doc.setFontSize(12);
    doc.text(filename.replace(/-/g, ' '), 14, 12);
    if (asOf) {
      doc.setFontSize(9);
      doc.text(`As of ${asOf}`, 14, 18);
    }
    autoTable(doc, {
      head,
      body,
      startY: asOf ? 22 : 16,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229] },
    });
    doc.save(`${filename}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 text-sm">
          {asOf && (
            <span className="rounded-full bg-indigo-50 border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700 whitespace-nowrap">
              As of {asOf}
            </span>
          )}
          <span className="text-slate-500">Show</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className={selectClass}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value={sorted.length || 1}>All</option>
          </select>
          <span className="text-slate-500">entries</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search…"
            className={inputClass}
          />
          <button type="button" onClick={downloadXls} disabled={sorted.length === 0} className={btnClass}>
            <IconDownload /> Download XLS
          </button>
          <button type="button" onClick={downloadPdf} disabled={sorted.length === 0} className={btnClass}>
            <IconPdf /> Download PDF
          </button>
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col)}
                  className={`px-4 py-3 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.sortable === false ? '' : 'cursor-pointer select-none hover:text-slate-700'}`}
                >
                  {col.label}
                  {col.sortable !== false && <IconSort direction={sort.key === col.key ? sort.direction : null} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center text-sm text-slate-400">
                  {loading ? 'Loading…' : search ? 'No rows match your search.' : emptyLabel}
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => (
                <tr key={getRowKey ? getRowKey(row, idx) : idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80 transition-colors">
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-2.5 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                      {col.render ? col.render(row) : String(defaultAccessor(row, col) ?? '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {sorted.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-slate-500">
          <span>
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sorted.length)} of {sorted.length}
            {sorted.length !== rows.length ? ` (filtered from ${rows.length})` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" className={btnClass} disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button type="button" className={btnClass} disabled={currentPage >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
